import { getFeatureFlagClientSingleton } from '@/helpers/feature-flag/model/base/Client';
import {
  FEATURE_FLAG_VALUES_RECEIVED,
  FEATURE_FLAGS,
  type FEATURE_FLAGS_ALL_VALUES,
  type FEATURE_FLAGS_VALUE,
} from '@/helpers/feature-flag/model/constants';
import { getStoredValue, removeStoredValue, setStoredValue } from '@/helpers/util';
import { isProdEnv } from '@/helpers/util/env';

const FF_FORCE_PREFIX = 'FF_FORCE';

const setFeatureFlagOverride = (flagKey: FEATURE_FLAGS_VALUE, value: boolean) => {
  if (isProdEnv()) return;

  try {
    const storageKey = `${FF_FORCE_PREFIX}/${flagKey}`;

    setStoredValue(storageKey, value);

    getFeatureFlagClientSingleton().clearCache();

    console.log(`🏁 Feature flag override: ${flagKey} = ${value}`);
  } catch (e) {
    console.warn('Failed to override feature flag:', e);
  }
};

const clearFeatureFlagOverrides = () => {
  if (isProdEnv()) return;

  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(FF_FORCE_PREFIX))
      .forEach((key) => removeStoredValue(key));

    getFeatureFlagClientSingleton().clearCache();

    console.log('🏁 All feature flag overrides cleared');
  } catch (e) {
    console.warn('Failed to clear overrides', e);
  }
};

const getAllBackendFlags = () => {
  const stored = getStoredValue(FEATURE_FLAG_VALUES_RECEIVED);

  return stored as FEATURE_FLAGS_ALL_VALUES;
};

const getAllStoredFlags = () => {
  const client = getFeatureFlagClientSingleton();

  const stored = {};

  Object.values(FEATURE_FLAGS).forEach((flagKey) => {
    const entry = client.getCachedValue(flagKey);

    //@ts-expect-error test
    stored[flagKey] = entry;
  });

  return stored;
};

export const getFeatureFlagOverride = (flagKey: FEATURE_FLAGS_VALUE) => {
  if (isProdEnv()) return null;

  try {
    const storageKey = `${FF_FORCE_PREFIX}/${flagKey}`;

    const stored = getStoredValue(storageKey);

    return stored as boolean | null;
  } catch (e) {
    return null;
  }
};

export const preserveAllFeatureFlags = (flagKey: FEATURE_FLAGS_VALUE, flagValue: boolean) => {
  if (isProdEnv()) return;

  const prevFeatureFlags = getStoredValue(FEATURE_FLAG_VALUES_RECEIVED);
  setStoredValue(FEATURE_FLAG_VALUES_RECEIVED, { ...prevFeatureFlags, [flagKey]: flagValue });
};

declare global {
  interface Window {
    __FF: {
      clearFeatureFlags: () => void;
      flags: typeof FEATURE_FLAGS;
      getAllBackendFlags: () => FEATURE_FLAGS_ALL_VALUES;
      getAllStoredFlags: () => unknown;
      setFeatureFlag: (key: FEATURE_FLAGS_VALUE, value: boolean) => void;
    };
  }
}

if (!isProdEnv() && typeof window !== 'undefined') {
  window.__FF = {
    clearFeatureFlags: clearFeatureFlagOverrides,
    flags: FEATURE_FLAGS,
    getAllBackendFlags: getAllBackendFlags,
    getAllStoredFlags: getAllStoredFlags,
    setFeatureFlag: setFeatureFlagOverride,
  };
}
